import type { DemographicCountry } from '../types/onelife.ts';

/**
 * NGUỒN DỮ LIỆU NHÂN KHẨU HỌC CHÍNH THỨC:
 * 1. United Nations Department of Economic and Social Affairs, Population Division
 *    - World Population Prospects 2024 Revision (WPP 2024)
 *    - Life expectancy at birth (e0 by sex, 2023-2024 estimate)
 *    - Annual Live Births (ước tính số ca sinh hằng năm 2023-2024)
 * 2. World Bank Open Data - World Development Indicators (2023/2024)
 *    - GDP per capita (current US$, 2023)
 *    - Urban population (% of total)
 * 3. UNDP Human Development Report (2023/2024 HDI Tiers)
 *
 * PHÂN BIỆT RÕ HAI MÔ HÌNH XÁC SUẤT:
 * - ANNUAL BIRTHS (Số ca sinh hằng năm): Xác suất một em bé sinh ra hôm nay trên Trái Đất thuộc quốc gia nào.
 *   Ví dụ: Nigeria sinh ~8.0 triệu bé/năm (6.0% toàn cầu), dù dân số chỉ chiếm 2.8%.
 * - TOTAL POPULATION (Quy mô dân số): Xác suất chọn ngẫu nhiên một con người đang còn sống trên Trái Đất.
 *   Ví dụ: Trung Quốc chiếm 17.5% dân số nhưng chỉ chiếm 6.7% số ca sinh mới vì tỷ lệ sinh thấp (~1.0 con/phụ nữ).
 */

export const DEMOGRAPHIC_DATA_SOURCE = {
  unSource: 'UN Population Division — World Population Prospects 2024 Revision',
  worldBankSource: 'World Bank Open Data — World Development Indicators 2023',
  version: '2024.1',
  lastUpdated: '2024-07-11',
};

export const WORLD_COUNTRIES: DemographicCountry[] = [
  {
    code: 'IN',
    nameVi: 'Ấn Độ',
    nameEn: 'India',
    populationMillion: 1428.6,
    populationWeight: 1428.6,
    annualBirthsMillion: 23.14,
    annualBirthsWeight: 23.14,
    lifeExpectancyMale: 65.8,
    lifeExpectancyFemale: 68.6,
    lifeExpectancyAverage: 67.2,
    gdpPerCapitaUsd: 2484,
    hdiTier: 'medium',
    urbanPercentage: 36,
    flagEmoji: '🇮🇳',
    continent: 'Asia',
  },
  {
    code: 'CN',
    nameVi: 'Trung Quốc',
    nameEn: 'China',
    populationMillion: 1411.8,
    populationWeight: 1411.8,
    annualBirthsMillion: 9.02,
    annualBirthsWeight: 9.02,
    lifeExpectancyMale: 75.3,
    lifeExpectancyFemale: 81.3,
    lifeExpectancyAverage: 78.2,
    gdpPerCapitaUsd: 12614,
    hdiTier: 'high',
    urbanPercentage: 65,
    flagEmoji: '🇨🇳',
    continent: 'Asia',
  },
  {
    code: 'US',
    nameVi: 'Hoa Kỳ',
    nameEn: 'United States',
    populationMillion: 339.9,
    populationWeight: 339.9,
    annualBirthsMillion: 3.66,
    annualBirthsWeight: 3.66,
    lifeExpectancyMale: 74.8,
    lifeExpectancyFemale: 80.2,
    lifeExpectancyAverage: 77.5,
    gdpPerCapitaUsd: 81695,
    hdiTier: 'very_high',
    urbanPercentage: 83,
    flagEmoji: '🇺🇸',
    continent: 'Americas',
  },
  {
    code: 'ID',
    nameVi: 'Indonesia',
    nameEn: 'Indonesia',
    populationMillion: 277.5,
    populationWeight: 277.5,
    annualBirthsMillion: 4.42,
    annualBirthsWeight: 4.42,
    lifeExpectancyMale: 68.3,
    lifeExpectancyFemale: 72.8,
    lifeExpectancyAverage: 70.5,
    gdpPerCapitaUsd: 4940,
    hdiTier: 'high',
    urbanPercentage: 58,
    flagEmoji: '🇮🇩',
    continent: 'Asia',
  },
  {
    code: 'PK',
    nameVi: 'Pakistan',
    nameEn: 'Pakistan',
    populationMillion: 240.5,
    populationWeight: 240.5,
    annualBirthsMillion: 6.48,
    annualBirthsWeight: 6.48,
    lifeExpectancyMale: 63.8,
    lifeExpectancyFemale: 68.1,
    lifeExpectancyAverage: 66.0,
    gdpPerCapitaUsd: 1407,
    hdiTier: 'medium',
    urbanPercentage: 37,
    flagEmoji: '🇵🇰',
    continent: 'Asia',
  },
  {
    code: 'NG',
    nameVi: 'Nigeria',
    nameEn: 'Nigeria',
    populationMillion: 223.8,
    populationWeight: 223.8,
    annualBirthsMillion: 7.98,
    annualBirthsWeight: 7.98,
    lifeExpectancyMale: 52.3,
    lifeExpectancyFemale: 53.1,
    lifeExpectancyAverage: 52.7,
    gdpPerCapitaUsd: 1621,
    hdiTier: 'low',
    urbanPercentage: 54,
    flagEmoji: '🇳🇬',
    continent: 'Africa',
  },
  {
    code: 'BR',
    nameVi: 'Brazil',
    nameEn: 'Brazil',
    populationMillion: 216.4,
    populationWeight: 216.4,
    annualBirthsMillion: 2.64,
    annualBirthsWeight: 2.64,
    lifeExpectancyMale: 70.3,
    lifeExpectancyFemale: 77.3,
    lifeExpectancyAverage: 73.8,
    gdpPerCapitaUsd: 10043,
    hdiTier: 'high',
    urbanPercentage: 88,
    flagEmoji: '🇧🇷',
    continent: 'Americas',
  },
  {
    code: 'BD',
    nameVi: 'Bangladesh',
    nameEn: 'Bangladesh',
    populationMillion: 172.9,
    populationWeight: 172.9,
    annualBirthsMillion: 3.02,
    annualBirthsWeight: 3.02,
    lifeExpectancyMale: 70.6,
    lifeExpectancyFemale: 74.3,
    lifeExpectancyAverage: 72.4,
    gdpPerCapitaUsd: 2529,
    hdiTier: 'medium',
    urbanPercentage: 40,
    flagEmoji: '🇧🇩',
    continent: 'Asia',
  },
  {
    code: 'RU',
    nameVi: 'Nga',
    nameEn: 'Russia',
    populationMillion: 144.2,
    populationWeight: 144.2,
    annualBirthsMillion: 1.26,
    annualBirthsWeight: 1.26,
    lifeExpectancyMale: 64.2,
    lifeExpectancyFemale: 74.8,
    lifeExpectancyAverage: 70.1,
    gdpPerCapitaUsd: 13817,
    hdiTier: 'very_high',
    urbanPercentage: 75,
    flagEmoji: '🇷🇺',
    continent: 'Europe',
  },
  {
    code: 'MX',
    nameVi: 'Mexico',
    nameEn: 'Mexico',
    populationMillion: 128.5,
    populationWeight: 128.5,
    annualBirthsMillion: 1.82,
    annualBirthsWeight: 1.82,
    lifeExpectancyMale: 68.8,
    lifeExpectancyFemale: 75.4,
    lifeExpectancyAverage: 72.1,
    gdpPerCapitaUsd: 13926,
    hdiTier: 'high',
    urbanPercentage: 81,
    flagEmoji: '🇲🇽',
    continent: 'Americas',
  },
  {
    code: 'ET',
    nameVi: 'Ethiopia',
    nameEn: 'Ethiopia',
    populationMillion: 126.5,
    populationWeight: 126.5,
    annualBirthsMillion: 4.12,
    annualBirthsWeight: 4.12,
    lifeExpectancyMale: 62.8,
    lifeExpectancyFemale: 67.2,
    lifeExpectancyAverage: 65.0,
    gdpPerCapitaUsd: 1027,
    hdiTier: 'low',
    urbanPercentage: 23,
    flagEmoji: '🇪🇹',
    continent: 'Africa',
  },
  {
    code: 'JP',
    nameVi: 'Nhật Bản',
    nameEn: 'Japan',
    populationMillion: 124.5,
    populationWeight: 124.5,
    annualBirthsMillion: 0.72,
    annualBirthsWeight: 0.72,
    lifeExpectancyMale: 81.1,
    lifeExpectancyFemale: 87.1,
    lifeExpectancyAverage: 84.1,
    gdpPerCapitaUsd: 33834,
    hdiTier: 'very_high',
    urbanPercentage: 92,
    flagEmoji: '🇯🇵',
    continent: 'Asia',
  },
  {
    code: 'PH',
    nameVi: 'Philippines',
    nameEn: 'Philippines',
    populationMillion: 117.3,
    populationWeight: 117.3,
    annualBirthsMillion: 2.15,
    annualBirthsWeight: 2.15,
    lifeExpectancyMale: 67.2,
    lifeExpectancyFemale: 71.5,
    lifeExpectancyAverage: 69.3,
    gdpPerCapitaUsd: 3725,
    hdiTier: 'medium',
    urbanPercentage: 48,
    flagEmoji: '🇵🇭',
    continent: 'Asia',
  },
  {
    code: 'EG',
    nameVi: 'Ai Cập',
    nameEn: 'Egypt',
    populationMillion: 112.7,
    populationWeight: 112.7,
    annualBirthsMillion: 2.11,
    annualBirthsWeight: 2.11,
    lifeExpectancyMale: 68.2,
    lifeExpectancyFemale: 72.8,
    lifeExpectancyAverage: 70.4,
    gdpPerCapitaUsd: 3512,
    hdiTier: 'high',
    urbanPercentage: 43,
    flagEmoji: '🇪🇬',
    continent: 'Africa',
  },
  {
    code: 'CD',
    nameVi: 'CHDC Congo',
    nameEn: 'DR Congo',
    populationMillion: 102.3,
    populationWeight: 102.3,
    annualBirthsMillion: 4.22,
    annualBirthsWeight: 4.22,
    lifeExpectancyMale: 57.3,
    lifeExpectancyFemale: 61.2,
    lifeExpectancyAverage: 59.2,
    gdpPerCapitaUsd: 649,
    hdiTier: 'low',
    urbanPercentage: 47,
    flagEmoji: '🇨🇩',
    continent: 'Africa',
  },
  {
    code: 'VN',
    nameVi: 'Việt Nam',
    nameEn: 'Vietnam',
    populationMillion: 100.3,
    populationWeight: 100.3,
    annualBirthsMillion: 1.35,
    annualBirthsWeight: 1.35,
    lifeExpectancyMale: 70.9,
    lifeExpectancyFemale: 76.8,
    lifeExpectancyAverage: 73.8,
    gdpPerCapitaUsd: 4347,
    hdiTier: 'high',
    urbanPercentage: 39,
    flagEmoji: '🇻🇳',
    continent: 'Asia',
  },
  {
    code: 'IR',
    nameVi: 'Iran',
    nameEn: 'Iran',
    populationMillion: 89.2,
    populationWeight: 89.2,
    annualBirthsMillion: 1.15,
    annualBirthsWeight: 1.15,
    lifeExpectancyMale: 71.2,
    lifeExpectancyFemale: 76.5,
    lifeExpectancyAverage: 73.8,
    gdpPerCapitaUsd: 4670,
    hdiTier: 'high',
    urbanPercentage: 77,
    flagEmoji: '🇮🇷',
    continent: 'Asia',
  },
  {
    code: 'TR',
    nameVi: 'Thổ Nhĩ Kỳ',
    nameEn: 'Turkey',
    populationMillion: 85.8,
    populationWeight: 85.8,
    annualBirthsMillion: 1.03,
    annualBirthsWeight: 1.03,
    lifeExpectancyMale: 73.5,
    lifeExpectancyFemale: 79.5,
    lifeExpectancyAverage: 76.5,
    gdpPerCapitaUsd: 12986,
    hdiTier: 'very_high',
    urbanPercentage: 77,
    flagEmoji: '🇹🇷',
    continent: 'Europe',
  },
  {
    code: 'DE',
    nameVi: 'Đức',
    nameEn: 'Germany',
    populationMillion: 84.4,
    populationWeight: 84.4,
    annualBirthsMillion: 0.69,
    annualBirthsWeight: 0.69,
    lifeExpectancyMale: 78.3,
    lifeExpectancyFemale: 83.2,
    lifeExpectancyAverage: 80.8,
    gdpPerCapitaUsd: 52746,
    hdiTier: 'very_high',
    urbanPercentage: 78,
    flagEmoji: '🇩🇪',
    continent: 'Europe',
  },
  {
    code: 'TH',
    nameVi: 'Thái Lan',
    nameEn: 'Thailand',
    populationMillion: 71.8,
    populationWeight: 71.8,
    annualBirthsMillion: 0.50,
    annualBirthsWeight: 0.50,
    lifeExpectancyMale: 71.8,
    lifeExpectancyFemale: 79.8,
    lifeExpectancyAverage: 75.8,
    gdpPerCapitaUsd: 7172,
    hdiTier: 'high',
    urbanPercentage: 53,
    flagEmoji: '🇹🇭',
    continent: 'Asia',
  },
  {
    code: 'GB',
    nameVi: 'Vương Quốc Anh',
    nameEn: 'United Kingdom',
    populationMillion: 67.7,
    populationWeight: 67.7,
    annualBirthsMillion: 0.67,
    annualBirthsWeight: 0.67,
    lifeExpectancyMale: 78.6,
    lifeExpectancyFemale: 82.6,
    lifeExpectancyAverage: 80.6,
    gdpPerCapitaUsd: 48867,
    hdiTier: 'very_high',
    urbanPercentage: 84,
    flagEmoji: '🇬🇧',
    continent: 'Europe',
  },
  {
    code: 'FR',
    nameVi: 'Pháp',
    nameEn: 'France',
    populationMillion: 64.8,
    populationWeight: 64.8,
    annualBirthsMillion: 0.67,
    annualBirthsWeight: 0.67,
    lifeExpectancyMale: 79.4,
    lifeExpectancyFemale: 85.2,
    lifeExpectancyAverage: 82.3,
    gdpPerCapitaUsd: 44461,
    hdiTier: 'very_high',
    urbanPercentage: 82,
    flagEmoji: '🇫🇷',
    continent: 'Europe',
  },
  {
    code: 'ZA',
    nameVi: 'Nam Phi',
    nameEn: 'South Africa',
    populationMillion: 60.4,
    populationWeight: 60.4,
    annualBirthsMillion: 1.15,
    annualBirthsWeight: 1.15,
    lifeExpectancyMale: 59.5,
    lifeExpectancyFemale: 64.2,
    lifeExpectancyAverage: 61.8,
    gdpPerCapitaUsd: 6253,
    hdiTier: 'high',
    urbanPercentage: 68,
    flagEmoji: '🇿🇦',
    continent: 'Africa',
  },
  {
    code: 'KR',
    nameVi: 'Hàn Quốc',
    nameEn: 'South Korea',
    populationMillion: 51.7,
    populationWeight: 51.7,
    annualBirthsMillion: 0.23,
    annualBirthsWeight: 0.23,
    lifeExpectancyMale: 79.9,
    lifeExpectancyFemale: 85.6,
    lifeExpectancyAverage: 82.7,
    gdpPerCapitaUsd: 33121,
    hdiTier: 'very_high',
    urbanPercentage: 82,
    flagEmoji: '🇰🇷',
    continent: 'Asia',
  },
  {
    code: 'CA',
    nameVi: 'Canada',
    nameEn: 'Canada',
    populationMillion: 40.1,
    populationWeight: 40.1,
    annualBirthsMillion: 0.36,
    annualBirthsWeight: 0.36,
    lifeExpectancyMale: 79.5,
    lifeExpectancyFemale: 83.9,
    lifeExpectancyAverage: 81.7,
    gdpPerCapitaUsd: 53372,
    hdiTier: 'very_high',
    urbanPercentage: 82,
    flagEmoji: '🇨🇦',
    continent: 'Americas',
  },
  {
    code: 'AU',
    nameVi: 'Úc',
    nameEn: 'Australia',
    populationMillion: 26.6,
    populationWeight: 26.6,
    annualBirthsMillion: 0.30,
    annualBirthsWeight: 0.30,
    lifeExpectancyMale: 81.2,
    lifeExpectancyFemale: 85.3,
    lifeExpectancyAverage: 83.2,
    gdpPerCapitaUsd: 64712,
    hdiTier: 'very_high',
    urbanPercentage: 86,
    flagEmoji: '🇦🇺',
    continent: 'Oceania',
  },
  {
    code: 'SE',
    nameVi: 'Thụy Điển',
    nameEn: 'Sweden',
    populationMillion: 10.5,
    populationWeight: 10.5,
    annualBirthsMillion: 0.10,
    annualBirthsWeight: 0.10,
    lifeExpectancyMale: 81.3,
    lifeExpectancyFemale: 84.8,
    lifeExpectancyAverage: 83.0,
    gdpPerCapitaUsd: 55536,
    hdiTier: 'very_high',
    urbanPercentage: 88,
    flagEmoji: '🇸🇪',
    continent: 'Europe',
  },
  {
    code: 'CH',
    nameVi: 'Thụy Sĩ',
    nameEn: 'Switzerland',
    populationMillion: 8.9,
    populationWeight: 8.9,
    annualBirthsMillion: 0.08,
    annualBirthsWeight: 0.08,
    lifeExpectancyMale: 81.8,
    lifeExpectancyFemale: 85.9,
    lifeExpectancyAverage: 83.8,
    gdpPerCapitaUsd: 99994,
    hdiTier: 'very_high',
    urbanPercentage: 74,
    flagEmoji: '🇨🇭',
    continent: 'Europe',
  },
  {
    code: 'NO',
    nameVi: 'Na Uy',
    nameEn: 'Norway',
    populationMillion: 5.5,
    populationWeight: 5.5,
    annualBirthsMillion: 0.05,
    annualBirthsWeight: 0.05,
    lifeExpectancyMale: 81.6,
    lifeExpectancyFemale: 84.7,
    lifeExpectancyAverage: 83.2,
    gdpPerCapitaUsd: 87962,
    hdiTier: 'very_high',
    urbanPercentage: 84,
    flagEmoji: '🇳🇴',
    continent: 'Europe',
  },
];

/**
 * Chọn ngẫu nhiên một quốc gia theo trọng số
 * @param mode 'annual_births' (mặc định - chuẩn UN ca sinh mới) hoặc 'population' (dân số thế giới hiện tại)
 */
export function rollRandomCountry(mode: 'annual_births' | 'population' = 'annual_births'): DemographicCountry {
  const isBirthMode = mode === 'annual_births';
  const totalWeight = WORLD_COUNTRIES.reduce(
    (sum, c) => sum + (isBirthMode ? c.annualBirthsWeight : c.populationWeight),
    0
  );
  let random = Math.random() * totalWeight;

  for (const country of WORLD_COUNTRIES) {
    const weight = isBirthMode ? country.annualBirthsWeight : country.populationWeight;
    if (random < weight) {
      return country;
    }
    random -= weight;
  }

  return WORLD_COUNTRIES[0];
}
